import { Button, message } from "antd";
import Form from "../../Form";
import styles from "./EstimateEdit.module.scss";
import {
  ButtonShape,
  ButtonSize,
  ButtonType,
} from "../../../enums/ButtonAttributesEnums";
import { buttonLabels } from "../QuoteEdit/constants";
import tnlogo from "../../../assets/images/TNLogo.svg";
import { useHistory, useLocation } from "react-router-dom";
import DocumentServices from "../../../services/documents.services";
import {
  estimateDocumentFieldNames,
  estimateNotificationMessages,
  getDefaultEstimateValues,
  estimateHeader,
  salesPersonFields,
} from "./constants";
import { appRoutes } from "../../../constants/appRoutes";
import TNLoader from "../../../reusable-components/TNLoader";
import InputField from "../../../reusable-components/TNInputField";
import { InputfieldTypes } from "../../../enums/InputFieldAttributesEnums";
import TableComponent from "../TableComponent";
import { hasAnyEmptyField } from "../../../utils/emptyStringCheck";

const EstimateEdit = () => {
  const location = useLocation();
  const navigate = useHistory();
  const id = location?.pathname?.split("/")?.pop();
  const { FetchDocumentDetails, SaveDocumentAttributes } = DocumentServices();
  const { data } = FetchDocumentDetails({});
  const { mutateAsync: saveDocumentAttributes, isPending: isSavePending } =
    SaveDocumentAttributes();
  const defaultValues = getDefaultEstimateValues(data, id);
  const handleCancel = () => {
    navigate.push(appRoutes.LIST_DOCUMENTS);
  };
  return (
    <>
      <TNLoader isLoading={isSavePending} />
      <Form defaultValues={defaultValues}>
        {({ getValues }) => {
          const buildPayload = () => {
            const payload: individualContent = {
              id: id,
              companyName: getValues(estimateDocumentFieldNames.companyName),
              companyAddress: getValues(
                estimateDocumentFieldNames.companyAddress
              ),
              companyContact: getValues(
                estimateDocumentFieldNames.companyContact
              ),
              mainDescription: getValues(
                estimateDocumentFieldNames.mainDescription
              ),
              estimateAddressTerms: getValues(
                estimateDocumentFieldNames.estimateAddressTerms
              ),
              truckingTerms: getValues(
                estimateDocumentFieldNames.truckingTerms
              ),
              paymentTerms: getValues(estimateDocumentFieldNames.paymentTerms),
              thanksNote: getValues(estimateDocumentFieldNames.thanksNote),
              estimateHeader: getValues(
                estimateDocumentFieldNames.estimateHeader
              ),
            };
            return payload;
          };
          const handleSave = async () => {
            const payload = buildPayload();
            if (hasAnyEmptyField(payload)) {
              message.error(estimateNotificationMessages.emptyFieldMessage);
              return;
            }
            await saveDocumentAttributes(payload, {
              onSuccess: () => {
                message.success(
                  estimateNotificationMessages.saveSuccessMessage
                );
                navigate.push(appRoutes.LIST_DOCUMENTS);
              },
              onError: () => {
                message.error(estimateNotificationMessages.saveFailureMessage);
              },
            });
          };
          return (
            <div className={styles.estimateEditTemplateContainer}>
              <div className={styles.buttonContainer}>
                <div className={styles.cancelButtonContainer}>
                  <Button
                    size={ButtonSize.LARGE}
                    shape={ButtonShape.DEFAULT}
                    onClick={handleCancel}
                  >
                    {buttonLabels.cancel}
                  </Button>
                </div>
                <div className={styles.saveButtonContainer}>
                  <Button
                    type={ButtonType.PRIMARY}
                    size={ButtonSize.LARGE}
                    shape={ButtonShape.DEFAULT}
                    onClick={handleSave}
                  >
                    {buttonLabels.save}
                  </Button>
                </div>
              </div>
              <div className={styles.estimateContentContainer}>
                <div className={styles.estimateLogoAndAddressContainer}>
                  <div className={styles.estimateLogoContainer}>
                    <img src={tnlogo} alt="" />
                  </div>
                  <div className={styles.estimateAddressContainer}>
                    <InputField
                      textAreaRows={1}
                      showCount
                      maxLength={45}
                      type={InputfieldTypes.TEXT_AREA}
                      name={estimateDocumentFieldNames.companyName}
                      className={styles.addressInputFields}
                    />
                    <InputField
                      textAreaRows={1}
                      showCount
                      maxLength={90}
                      type={InputfieldTypes.TEXT_AREA}
                      name={estimateDocumentFieldNames.companyAddress}
                      className={styles.addressInputFields}
                    />
                    <InputField
                      textAreaRows={1}
                      showCount
                      maxLength={45}
                      type={InputfieldTypes.TEXT_AREA}
                      name={estimateDocumentFieldNames.companyContact}
                      className={styles.addressInputFields}
                    />
                  </div>
                </div>
                <div className={styles.estimateHeaderContainer}>
                  <div className={styles.estimateHeader}>
                    <InputField
                      textAreaRows={1}
                      showCount
                      maxLength={30}
                      type={InputfieldTypes.TEXT_AREA}
                      name={estimateDocumentFieldNames.estimateHeader}
                    />
                  </div>
                  <div className={styles.estimateAddressTerms}>
                    <InputField
                      textAreaRows={6}
                      showCount
                      maxLength={1000}
                      type={InputfieldTypes.TEXT_AREA}
                      name={estimateDocumentFieldNames.mainDescription}
                    />
                    <InputField
                      textAreaRows={1}
                      showCount
                      maxLength={125}
                      type={InputfieldTypes.TEXT_AREA}
                      name={estimateDocumentFieldNames.estimateAddressTerms}
                    />
                  </div>
                </div>
                <div className={styles.table}>
                  <TableComponent />
                </div>
                <div className={styles.estimateDescriptionContainer}>
                  <InputField
                    textAreaRows={6}
                    showCount
                    maxLength={1000}
                    type={InputfieldTypes.TEXT_AREA}
                    name={estimateDocumentFieldNames.truckingTerms}
                  />
                </div>
                <div className={styles.estimateBottomSection}>
                  <div className={styles.estimatePaymentTerms}>
                    <div className={styles.contentHeader}>
                      {estimateHeader.paymentHeader}
                    </div>
                    <InputField
                      textAreaRows={11}
                      showCount
                      maxLength={500}
                      type={InputfieldTypes.TEXT_AREA}
                      name={estimateDocumentFieldNames.paymentTerms}
                    />
                  </div>
                  <div className={styles.estimateBottomRightSection}>
                    <div className={styles.contentHeader}>
                      {estimateHeader.thankyou}
                    </div>
                    <div className={styles.estimateThanksTerms}>
                      <InputField
                        textAreaRows={3}
                        showCount
                        maxLength={500}
                        type={InputfieldTypes.TEXT_AREA}
                        name={estimateDocumentFieldNames.thanksNote}
                      />
                    </div>
                    <div className={styles.estimateSalesPersonFields}>
                      <div className={styles.estimateSalesPersonTopFields}>
                        <div>{salesPersonFields.salesPerson}</div>
                        <div>{salesPersonFields.phone}</div>
                      </div>
                      <div className={styles.estimateSalesPersonBottomFields}>
                        {salesPersonFields.email}
                      </div>
                    </div>
                    <div className={styles.estimateAccountDepartmentContainer}>
                      <div className={styles.accountingHeader}>
                        {estimateHeader.accountingDepartment}
                      </div>
                      <div>{estimateHeader.accountingDepartmentContent}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      </Form>
    </>
  );
};
export default EstimateEdit;
